import { inject, Injectable, signal } from '@angular/core'
import {
   AppPractice,
   HospitalPrice,
   Practice,
   PracticeBatchInfo,
   PracticesClient,
   PricingManagerClient,
   Site,
   VetcorPricingCaptainDto,
} from '../shared/services/ppv.api'
import { forkJoin, tap } from 'rxjs'

@Injectable({
   providedIn: 'root',
})
export class HospitalPricingService {
   private pricingApiClient = inject(PricingManagerClient)
   private practicesController = inject(PracticesClient)

   hospitalPrices = signal<HospitalPrice[]>(undefined)
   sites = signal<Site[]>(undefined)
   activePractice = signal<Practice>(undefined)
   activeCycle = signal<PracticeBatchInfo>(undefined)
   pricingCaptain = signal<VetcorPricingCaptainDto>(undefined)

   loadHospitalPrices(practiceSKey: number) {
      forkJoin([
         this.pricingApiClient.getPrices(practiceSKey),
         this.pricingApiClient.getBatchInfo(practiceSKey),
         this.pricingApiClient.getPricingCaptain(practiceSKey),
         this.practicesController.getPractice(practiceSKey),
      ])
         .pipe(
            tap(([prices, batchInfo, pricingCaptain, practice]) => {
               prices = prices.sort((a, b) => {
                  // Assign sort rank: 0 = NEW, 2 = SF, 1 = normal
                  const getRank = (p: any) => (!!p.newCodeRequest && !!p.newCodeRequestId ? 0 : p.recordType === 'SF' ? 2 : 1)

                  const rankA = getRank(a)
                  const rankB = getRank(b)

                  if (rankA !== rankB) return rankA - rankB
                  // If ranks are equal, sort by revenue descending
                  const aRev = a.metrics?.l12MRevenueContributionPct ?? 0
                  const bRev = b.metrics?.l12MRevenueContributionPct ?? 0
                  return bRev - aRev
               })

               this.hospitalPrices.set(prices)
               this.activeCycle.set(batchInfo)
               this.pricingCaptain.set(pricingCaptain)
               this.activePractice.set(practice)
            })
         )
         .subscribe()
   }

   loadSites(practiceSKey: number) {
      this.practicesController
         .getSites(practiceSKey)
         .pipe(
            tap((sites) => {
               this.sites.set(sites)
            })
         )
         .subscribe()
   }

   rejectRemainingPricingUpdateRequests(batchId: number) {
      this.pricingApiClient
         .rejectRemainingPricingUpdateRequests(batchId)
         .pipe(
            tap(() => {
               this.activeCycle.update((ac) => ({ ...ac, batchStatus: 3 }))
            })
         )
         .subscribe()
   }
   approveRemainingPricingUpdateRequests(batchId: number) {
      this.pricingApiClient
         .approveRemainingPricingUpdateRequests(batchId)
         .pipe(
            tap(() => {
               this.activeCycle.update((ac) => ({ ...ac, batchStatus: 2 }))
            })
         )
         .subscribe()
   }
}
